import { BlockStack, Icon, InlineStack, RadioButton, Select, Text, TextField } from '@shopify/polaris';
import { ProductIcon, StoreIcon } from '@shopify/polaris-icons';
import { languageOptions } from '@/constants/options';
import LanguageAutocomplete from '@/pages/Languages/components/LanguageAutocomplete';
import { IntegrationConfigTab } from '../../types';
import { CardGrid, LocaleModeList, LocaleModeOption, OptionCard, OptionHeader, SectionDivider } from './styled';

const REVIEW_TARGET_OPTIONS = [
  {
    value: 'store',
    label: 'Store',
    description: 'Ask customers to review their experience with your store.',
    icons: [StoreIcon],
  },
  { value: 'product', label: 'Product', description: 'Ask customers to review purchased products.', icons: [ProductIcon] },
  {
    value: 'both',
    label: 'Store & Product',
    description: 'Ask for both store and product reviews in the same invitation.',
    icons: [StoreIcon, ProductIcon],
  },
];

const TARGET_BLOCKS = [
  { target: 'store', title: 'Store review', templateKey: 'storeTemplateId', templateLabel: 'Store review template', sendTimeKey: 'storePreferredSendTime' },
  {
    target: 'product',
    title: 'Product review',
    templateKey: 'productTemplateId',
    templateLabel: 'Product review template',
    sendTimeKey: 'productPreferredSendTime',
  },
];

const SENDER_FIELDS = [
  { key: 'senderName', label: 'Sender name' },
  { key: 'senderEmail', label: 'Sender email' },
  { key: 'replyTo', label: 'Reply-to email' },
];

const CUSTOMER_LOCALE_PRESETS = ['customer', 'shop'];
const DEFAULT_MANUAL_LOCALE = 'en';

const CUSTOMER_LOCALE_OPTIONS = [
  {
    value: 'customer',
    label: "Customer's language",
    description: "Use each customer's own language automatically.",
  },
  {
    value: 'shop',
    label: 'Shop language',
    description: "Use the store's default language for every invitation.",
  },
  {
    value: 'manual',
    label: 'Choose a language',
    description: 'Always send in one specific language, regardless of the customer.',
  },
];

const TargetFields = ({ block, config }: { block: (typeof TARGET_BLOCKS)[number]; config: IntegrationConfigTab }) => (
  <>
    <Select
      label={block.templateLabel}
      options={config.options[block.templateKey] || []}
      value={config.values[block.templateKey]}
      disabled={!config.isEditing || config.isLoading}
      helpText={config.options[block.templateKey]?.find((o) => o.value === config.values[block.templateKey])?.hint}
      onChange={(value) => config.onChange(block.templateKey, value)}
    />
    <Select
      label="Send invitation"
      options={config.options.preferredSendTime}
      value={config.values[block.sendTimeKey]}
      disabled={!config.isEditing}
      onChange={(value) => config.onChange(block.sendTimeKey, value)}
    />
  </>
);

const TrustpilotConfigView = ({ config }: { config: IntegrationConfigTab }) => {
  const reviewTarget = config.values.reviewTarget;
  const showBothBlocks = reviewTarget === 'both';
  const activeBlock = TARGET_BLOCKS.find((block) => block.target === reviewTarget);

  const customerLocaleValue = config.values.customerEmailLocale;
  const customerLocaleMode = CUSTOMER_LOCALE_PRESETS.includes(customerLocaleValue) ? customerLocaleValue : 'manual';
  const manualLocale = customerLocaleMode === 'manual' ? customerLocaleValue : '';

  return (
    <BlockStack gap="400">
      <BlockStack gap="100">
        <Text as="h3" variant="headingMd">
          Review invitation settings
        </Text>
        <Text as="p" tone="subdued">
          Configure how Trustpilot asks your customers for reviews.
        </Text>
      </BlockStack>

      <BlockStack gap="200">
        <Text as="span" fontWeight="semibold">
          What would you like customers to review?
        </Text>
        <CardGrid>
          {REVIEW_TARGET_OPTIONS.map(({ value, label, description, icons }) => (
            <OptionCard key={value} $selected={reviewTarget === value}>
              <OptionHeader>
                <RadioButton
                  label=""
                  labelHidden
                  name="reviewTarget"
                  checked={reviewTarget === value}
                  disabled={!config.isEditing}
                  onChange={() => config.onChange('reviewTarget', value)}
                />
                <InlineStack gap="100" blockAlign="center">
                  {icons.map((icon, index) => (
                    <Icon key={index} source={icon} />
                  ))}
                </InlineStack>
              </OptionHeader>
              <Text as="span" fontWeight="semibold">
                {label}
              </Text>
              <Text as="span" tone="subdued" variant="bodySm">
                {description}
              </Text>
            </OptionCard>
          ))}
        </CardGrid>
      </BlockStack>

      {showBothBlocks ? (
        <BlockStack gap="400">
          {TARGET_BLOCKS.map((block) => (
            <BlockStack gap="200" key={block.target}>
              <SectionDivider>
                <Text as="span" fontWeight="semibold" tone="subdued">
                  {block.title}
                </Text>
              </SectionDivider>
              <TargetFields block={block} config={config} />
            </BlockStack>
          ))}
        </BlockStack>
      ) : (
        activeBlock && <TargetFields block={activeBlock} config={config} />
      )}

      <BlockStack gap="200">
        <Text as="span" fontWeight="semibold">
          Email sender
        </Text>
        {SENDER_FIELDS.map(({ key, label }) => (
          <TextField
            key={key}
            label={label}
            autoComplete="off"
            value={config.values[key]}
            disabled={!config.isEditing}
            onChange={(value) => config.onChange(key, value)}
          />
        ))}
        <Select
          label="Location"
          options={config.options.senderLocationId || []}
          value={config.values.senderLocationId}
          disabled={!config.isEditing || config.isLoading}
          helpText="Attribute invitations sent from this address to a specific Trustpilot business location."
          onChange={(value) => config.onChange('senderLocationId', value)}
        />
      </BlockStack>

      <BlockStack gap="200">
        <BlockStack gap="100">
          <Text as="span" fontWeight="semibold">
            Email customer
          </Text>
          <Text as="p" tone="subdued" variant="bodySm">
            Choose which language Trustpilot uses when it emails the customer.
          </Text>
        </BlockStack>
        <LocaleModeList>
          {CUSTOMER_LOCALE_OPTIONS.map((option) => (
            <LocaleModeOption key={option.value} $selected={customerLocaleMode === option.value}>
              <RadioButton
                label=""
                labelHidden
                name="customerEmailLocaleMode"
                checked={customerLocaleMode === option.value}
                disabled={!config.isEditing}
                onChange={() =>
                  config.onChange('customerEmailLocale', option.value === 'manual' ? manualLocale || DEFAULT_MANUAL_LOCALE : option.value)
                }
              />
              <BlockStack gap="050">
                <Text as="span" fontWeight="medium">
                  {option.label}
                </Text>
                <Text as="span" tone="subdued" variant="bodySm">
                  {option.description}
                </Text>
              </BlockStack>
            </LocaleModeOption>
          ))}
        </LocaleModeList>
        {customerLocaleMode === 'manual' && (
          <LanguageAutocomplete
            label="Language"
            options={languageOptions}
            selected={manualLocale}
            disabled={!config.isEditing}
            onChange={(value) => config.onChange('customerEmailLocale', value)}
            placeholder="Search languages"
          />
        )}
      </BlockStack>
    </BlockStack>
  );
};

export default TrustpilotConfigView;
